import { DefaultTheme } from 'styled-components';
import { defaultTheme } from '@styles/theme.styled';
import { ThemeConfigType } from './theme-config.type';

const {
  backgroundColors,
  borderRadius,
  fontFamily,
  fontSize,
  fontWeights,
  lineHeights,
  borderWidths,
  buttonVariants,
  inputSize,
  zIndex,
} = defaultTheme;

export default ({
  background_colors,
  border_radius,
  border_widths,
  breakpoints,
  button_variants,
  colors,
  font_family,
  font_size_desktop,
  font_size_mobile,
  font_weights,
  input_size_lg,
  input_size_md,
  input_size_sm,
  line_heights,
  padding,
  scales,
  shadows,
  spacing,
  transitions,
  z_index,
}: ThemeConfigType): DefaultTheme => {
  // Ensure arrays have at least one element with proper type assertions
  const firstBackgroundColor = background_colors[0] || {};
  const firstBorderRadius = border_radius[0] || {};
  const firstBorderWidths = border_widths[0] || {};
  const firstBreakpoints = breakpoints[0] || {};
  const firstButtonVariants = button_variants[0] || {};
  const firstColors = colors[0] || {};
  const firstFontFamily = font_family[0] || {};
  const firstFontSizeDesktop = font_size_desktop[0] || {};
  const firstFontSizeMobile = font_size_mobile[0] || {};
  const firstFontWeights = font_weights[0] || {};
  const firstInputSizeLg = input_size_lg[0] || {};
  const firstInputSizeMd = input_size_md[0] || {};
  const firstInputSizeSm = input_size_sm[0] || {};
  const firstLineHeights = line_heights[0] || {};
  const firstPadding = padding[0] || {};
  const firstScales = scales[0] || {};
  const firstShadows = shadows[0] || {};
  const firstSpacing = spacing[0] || {};
  const firstTransitions = transitions[0] || {};
  const firstZIndex = z_index[0] || {};

  return {
    backgroundColors: {
      transparent: backgroundColors.transparent,
      white: firstBackgroundColor.white || backgroundColors.white,
      light: firstBackgroundColor.light || backgroundColors.light,
      dark: firstBackgroundColor.dark || backgroundColors.dark,
      primary: firstBackgroundColor.primary || backgroundColors.primary,
      gradient: backgroundColors.gradient,
    },

    borderRadius: {
      small: firstBorderRadius.small || borderRadius.small,
      normal: firstBorderRadius.normal || borderRadius.normal,
      large: firstBorderRadius.large || borderRadius.large,
    },

    fontFamily: {
      display: firstFontFamily.display || fontFamily.display,
      text: firstFontFamily.text || fontFamily.text,
      additional: firstFontFamily.additional || fontFamily.additional,
    },

    fontSize: {
      tooltip: {
        desktop: firstFontSizeDesktop.tooltip || fontSize.tooltip.desktop,
        mobile: firstFontSizeMobile.tooltip || fontSize.tooltip.mobile,
      },
      extraSmall: {
        desktop: firstFontSizeDesktop.extra_small || fontSize.extraSmall.desktop,
        mobile: firstFontSizeMobile.extra_small || fontSize.extraSmall.mobile,
      },
      small: {
        desktop: firstFontSizeDesktop.small || fontSize.small.desktop,
        mobile: firstFontSizeMobile.small || fontSize.small.mobile,
      },
      body: {
        desktop: firstFontSizeDesktop.body || fontSize.body.desktop,
        mobile: firstFontSizeMobile.body || fontSize.body.mobile,
      },
      large: {
        desktop: firstFontSizeDesktop.large || fontSize.large.desktop,
        mobile: firstFontSizeMobile.large || fontSize.large.mobile,
      },
      eyebrow: {
        desktop: firstFontSizeDesktop.eyebrow || fontSize.eyebrow.desktop,
        mobile: firstFontSizeMobile.eyebrow || fontSize.eyebrow.mobile,
      },
      subheading: {
        desktop: firstFontSizeDesktop.subheading || fontSize.subheading.desktop,
        mobile: firstFontSizeMobile.subheading || fontSize.subheading.mobile,
      },
      heading6: {
        desktop: firstFontSizeDesktop.h6 || fontSize.heading6.desktop,
        mobile: firstFontSizeMobile.h6 || fontSize.heading6.mobile,
      },
      heading5: {
        desktop: firstFontSizeDesktop.h5 || fontSize.heading5.desktop,
        mobile: firstFontSizeMobile.h5 || fontSize.heading5.mobile,
      },
      heading4: {
        desktop: firstFontSizeDesktop.h4 || fontSize.heading4.desktop,
        mobile: firstFontSizeMobile.h4 || fontSize.heading4.mobile,
      },
      heading3: {
        desktop: firstFontSizeDesktop.h3 || fontSize.heading3.desktop,
        mobile: firstFontSizeMobile.h3 || fontSize.heading3.mobile,
      },
      heading2: {
        desktop: firstFontSizeDesktop.h2 || fontSize.heading2.desktop,
        mobile: firstFontSizeMobile.h2 || fontSize.heading2.mobile,
      },
      heading1: {
        desktop: firstFontSizeDesktop.h1 || fontSize.heading1.desktop,
        mobile: firstFontSizeMobile.h1 || fontSize.heading1.mobile,
      },
    },

    fontWeights: {
      normal: firstFontWeights.normal || fontWeights.normal,
      bold: firstFontWeights.bold || fontWeights.bold,
    },

    lineHeights: {
      xs: firstLineHeights.xs || lineHeights.xs,
      sm: firstLineHeights.sm || lineHeights.sm,
      base: firstLineHeights.base || lineHeights.base,
      lg: firstLineHeights.lg || lineHeights.lg,
      xl: firstLineHeights.xl || lineHeights.xl,
    },

    spacing: {
      none: firstSpacing.none || defaultTheme.spacing.none,
      xxs: firstSpacing.xxs || defaultTheme.spacing.xxs,
      xs: firstSpacing.xs || defaultTheme.spacing.xs,
      sm: firstSpacing.sm || defaultTheme.spacing.sm,
      md: firstSpacing.md || defaultTheme.spacing.md,
      lg: firstSpacing.lg || defaultTheme.spacing.lg,
      xl: firstSpacing.xl || defaultTheme.spacing.xl,
      xxl: firstSpacing.xxl || defaultTheme.spacing.xxl,
      xxxl: firstSpacing.xxxl || defaultTheme.spacing.xxxl,
      columnGap: firstSpacing.column_gap || defaultTheme.spacing.columnGap,
    },

    padding: {
      xs: firstPadding.xs || defaultTheme.padding.xs,
      sm: firstPadding.sm || defaultTheme.padding.sm,
      md: firstPadding.md || defaultTheme.padding.md,
      lg: firstPadding.lg || defaultTheme.padding.lg,
      xl: firstPadding.xl || defaultTheme.padding.xl,
      cards: {
        sm: firstPadding.sm_cards || defaultTheme.padding.cards.sm,
        md: firstPadding.md_cards || defaultTheme.padding.cards.md,
        lg: firstPadding.lg_cards || defaultTheme.padding.cards.lg,
      },
    },

    colors: {
      transparent: defaultTheme.colors.transparent,
      white: firstColors.white || defaultTheme.colors.white,
      black: firstColors.black || defaultTheme.colors.black,
      primary: firstColors.primary || defaultTheme.colors.primary,
      primaryLight: firstColors.primary_light || defaultTheme.colors.primaryLight,
      secondary: firstColors.secondary || defaultTheme.colors.secondary,
      tertiary: firstColors.tertiary || defaultTheme.colors.tertiary,
      accent: firstColors.accent || defaultTheme.colors.accent,
      accentSecondary: firstColors.accent_secondary || defaultTheme.colors.accentSecondary,
      dark: firstColors.dark || defaultTheme.colors.dark,
      light: firstColors.light || defaultTheme.colors.light,
      muted: firstColors.muted || defaultTheme.colors.muted,
      lightBlue: firstColors.light_blue || defaultTheme.colors.lightBlue,
      lightGreen: firstColors.light_green || defaultTheme.colors.lightGreen,
      lightGray: firstColors.light_gray || defaultTheme.colors.lightGray,
      blueSecondary: firstColors.blue_secondary || defaultTheme.colors.blueSecondary,
      lightPurple: firstColors.light_purple || defaultTheme.colors.lightPurple,
      darkPurple: firstColors.dark_purple || defaultTheme.colors.darkPurple,
      brand: firstColors.brand || defaultTheme.colors.brand,
    },

    borderWidths: {
      ui: firstBorderWidths.ui || borderWidths.ui,
      icons: firstBorderWidths.icons || borderWidths.icons,
    },

    breakpoints: {
      xs: firstBreakpoints.xs || defaultTheme.breakpoints.xs,
      sm: firstBreakpoints.sm || defaultTheme.breakpoints.sm,
      md: firstBreakpoints.md || defaultTheme.breakpoints.md,
      lg: firstBreakpoints.lg || defaultTheme.breakpoints.lg,
      xl: firstBreakpoints.xl || defaultTheme.breakpoints.xl,
      xxl: firstBreakpoints.xxl || defaultTheme.breakpoints.xxl,
    },

    buttonVariants: {
      primary: firstButtonVariants.primary || buttonVariants.primary,
      accent: firstButtonVariants.accent || buttonVariants.accent,
      white: firstButtonVariants.white || buttonVariants.white,
      dark: firstButtonVariants.dark || buttonVariants.dark,
    },

    inputSize: {
      sm: {
        desktop: {
          height: firstInputSizeSm.desktop_height || inputSize.sm.desktop.height,
          fontSize: firstInputSizeSm.desktop_font_size || inputSize.sm.desktop.fontSize,
          borderRadius: firstInputSizeSm.desktop_border_radius || inputSize.sm.desktop.borderRadius,
        },
        mobile: {
          height: firstInputSizeSm.mobile_height || inputSize.sm.mobile.height,
          fontSize: firstInputSizeSm.mobile_font_size || inputSize.sm.mobile.fontSize,
          borderRadius: firstInputSizeSm.mobile_border_radius || inputSize.sm.mobile.borderRadius,
        },
      },
      md: {
        desktop: {
          height: firstInputSizeMd.desktop_height || inputSize.md.desktop.height,
          fontSize: firstInputSizeMd.desktop_font_size || inputSize.md.desktop.fontSize,
          borderRadius: firstInputSizeMd.desktop_border_radius || inputSize.md.desktop.borderRadius,
        },
        mobile: {
          height: firstInputSizeMd.mobile_height || inputSize.md.mobile.height,
          fontSize: firstInputSizeMd.mobile_font_size || inputSize.md.mobile.fontSize,
          borderRadius: firstInputSizeMd.mobile_border_radius || inputSize.md.mobile.borderRadius,
        },
      },
      lg: {
        desktop: {
          height: firstInputSizeLg.desktop_height || inputSize.lg.desktop.height,
          fontSize: firstInputSizeLg.desktop_font_size || inputSize.lg.desktop.fontSize,
          borderRadius: firstInputSizeLg.desktop_border_radius || inputSize.lg.desktop.borderRadius,
        },
        mobile: {
          height: firstInputSizeLg.mobile_height || inputSize.lg.mobile.height,
          fontSize: firstInputSizeLg.mobile_font_size || inputSize.lg.mobile.fontSize,
          borderRadius: firstInputSizeLg.mobile_border_radius || inputSize.lg.mobile.borderRadius,
        },
      },
    },

    scales: {
      xs: firstScales.xs || defaultTheme.scales.xs,
      sm: firstScales.sm || defaultTheme.scales.sm,
      md: firstScales.md || defaultTheme.scales.md,
      lg: firstScales.lg || defaultTheme.scales.lg,
      xl: firstScales.xl || defaultTheme.scales.xl,
      xxl: firstScales.xxl || defaultTheme.scales.xxl,
    },

    shadows: {
      small: firstShadows.small || defaultTheme.shadows.small,
      normal: firstShadows.normal || defaultTheme.shadows.normal,
    },

    transitions: {
      short: firstTransitions.short || defaultTheme.transitions.short,
      normal: firstTransitions.normal || defaultTheme.transitions.normal,
      long: firstTransitions.long || defaultTheme.transitions.long,
    },

    zIndex: {
      navbar: firstZIndex.navbar || zIndex.navbar,
      surface: firstZIndex.surface || zIndex.surface,
      layeredGraphic: firstZIndex.layered_graphic || zIndex.layeredGraphic,
      layeredContent: firstZIndex.layered_content || zIndex.layeredContent,
      aboveLayers: firstZIndex.above_layers || zIndex.aboveLayers,
    },
  };
};
